import React, { useEffect, useRef, useState } from 'react';
import axiosRestaurantInstance from '../restaurant/AxiosRestaurantInstance';

const SEARCH_DEBOUNCE_MS = 350;

export const EMPTY_ADDRESS = {
  addressLine1: '',
  addressLine2: '',
  areaName: '',
  city: '',
  state: '',
  postalCode: '',
  country: '',
  lat: '',
  lon: '',
};

export const addressFromApi = (raw) => {
  if (!raw || typeof raw !== 'object') return { ...EMPTY_ADDRESS };
  return {
    addressLine1: raw.addressLine1 || raw.address_line1 || '',
    addressLine2: raw.addressLine2 || raw.address_line2 || '',
    areaName: raw.areaName || raw.area_name || '',
    city: raw.city || '',
    state: raw.state || '',
    postalCode: raw.postalCode || raw.postal_code || '',
    country: raw.country || '',
    lat: raw.lat ?? raw.latitude ?? '',
    lon: raw.lon ?? raw.lng ?? raw.longitude ?? '',
  };
};

export const isAddressComplete = (address) =>
  Boolean(
    String(address?.addressLine1 || '').trim() &&
      String(address?.city || '').trim() &&
      String(address?.state || '').trim()
  );

export const formatAddressLine = (address) =>
  [address?.addressLine1, address?.addressLine2, address?.areaName, address?.city, address?.state, address?.postalCode]
    .map((part) => String(part || '').trim())
    .filter(Boolean)
    .join(', ');

/**
 * Google-backed address picker: the address is only accepted once a suggestion is selected,
 * so the stored lines, city, state and coordinates always come from Places details.
 */
export default function AddressSearchFields({ value, onChange, disabled = false, label = 'Address' }) {
  const address = value || EMPTY_ADDRESS;
  const [query, setQuery] = useState(() => formatAddressLine(address));
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const timeoutRef = useRef(null);

  useEffect(() => () => timeoutRef.current && clearTimeout(timeoutRef.current), []);

  const handleQueryChange = (e) => {
    const text = e.target.value;
    setQuery(text);
    setSuggestions([]);
    setMessage('');
    if (isAddressComplete(address)) onChange({ ...EMPTY_ADDRESS });

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (text.trim().length < 2) return;

    timeoutRef.current = setTimeout(async () => {
      timeoutRef.current = null;
      setLoading(true);
      try {
        const res = await axiosRestaurantInstance.get('/address/suggest', { params: { address: text.trim() } });
        const list = res.data?.suggestions || res.data?.predictions || [];
        const rows = Array.isArray(list) ? list : [];
        setSuggestions(rows);
        setMessage(rows.length ? '' : 'No matching addresses. Try a different search.');
      } catch (err) {
        setSuggestions([]);
        setMessage(
          err?.response?.status === 404
            ? 'No matching addresses. Try a different search.'
            : err?.response?.data?.error || 'Address search failed. Please try again.'
        );
      } finally {
        setLoading(false);
      }
    }, SEARCH_DEBOUNCE_MS);
  };

  const handleSelect = async (placeId, text) => {
    try {
      const res = await axiosRestaurantInstance.get('/address/suggest/details', { params: { placeId } });
      const details = res.data;
      if (!details) {
        setMessage('Address details not found. Pick another suggestion.');
        return;
      }
      const next = {
        addressLine1: details.addressLine1 || '',
        addressLine2: address.addressLine2 || '',
        areaName: details.areaName || details.area || '',
        city: details.city || '',
        state: details.state || '',
        postalCode: details.postalCode || '',
        country: details.country || '',
        lat: details.latitude ?? details.lat ?? '',
        lon: details.longitude ?? details.lon ?? '',
      };
      setSuggestions([]);
      setQuery(text || details.formattedAddress || next.addressLine1);
      if (!next.city || !next.state) {
        setMessage('That address has no city or state. Pick a more specific address.');
        onChange({ ...EMPTY_ADDRESS });
        return;
      }
      setMessage('');
      onChange(next);
    } catch (err) {
      setMessage('Could not load that address. Pick another suggestion.');
    }
  };

  if (disabled) {
    return (
      <div className="employees-field employees-field--wide">
        <span>{label}</span>
        <div className="eats-admin-input address-search-fields__readonly">
          {formatAddressLine(address) || '-'}
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="employees-field employees-field--wide address-search-fields__search">
        <span>{label} *</span>
        <input
          className="eats-admin-input"
          aria-label={`Search ${label.toLowerCase()}`}
          type="text"
          value={query}
          onChange={handleQueryChange}
          placeholder="Type to search address..."
          autoComplete="off"
        />
        {loading ? <small className="address-search-fields__hint">Searching...</small> : null}
        {!loading && message ? (
          <small className="address-search-fields__hint address-search-fields__hint--error">{message}</small>
        ) : null}
        {suggestions.length > 0 ? (
          <ul className="address-search-fields__suggestions" role="listbox">
            {suggestions.map((s, index) => {
              const placeId = s.place_id || s.placeId;
              const text = s.description || s.formatted_address || '';
              return (
                <li key={placeId || index} role="option" aria-selected="false">
                  <button type="button" onClick={() => handleSelect(placeId, text)}>
                    {text}
                  </button>
                </li>
              );
            })}
          </ul>
        ) : null}
      </div>
      <label className="employees-field">
        <span>Flat / Floor / Landmark</span>
        <input
          className="eats-admin-input"
          type="text"
          value={address.addressLine2}
          onChange={(e) => onChange({ ...address, addressLine2: e.target.value })}
          placeholder="Optional"
        />
      </label>
      <label className="employees-field">
        <span>City</span>
        <input className="eats-admin-input" value={address.city} readOnly placeholder="From address search" />
      </label>
      <label className="employees-field">
        <span>State</span>
        <input className="eats-admin-input" value={address.state} readOnly placeholder="From address search" />
      </label>
      <label className="employees-field">
        <span>Postal Code</span>
        <input className="eats-admin-input" value={address.postalCode} readOnly placeholder="From address search" />
      </label>
    </>
  );
}
